import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import PublicPageHeader from "../components/PublicPageHeader";
import useDocumentMeta from "../lib/useDocumentMeta";

/**
 * /links — Instagram link-in-bio shim. One static page, no backend, no
 * state: the shared slate-navy public header (logo + wordmark, no nav),
 * two lines of context, and six tappable destination cards over the
 * homepage's soft mint gradient + grid. Mobile-first at 390px.
 *
 * `noindex` on purpose — this is a navigation shim, not content, and it
 * should not compete with the real pages in search. Title + robots meta
 * are set locally on mount and torn down on unmount so nothing leaks
 * across SPA navigations.
 */

const LINKS = [
  {
    title: "Free Transferable Skills posters",
    sub: "Print-ready PDFs. No sign-up.",
    to: "/posters",
    testid: "links-card-posters",
  },
  {
    title: "Make a free lesson plan",
    sub: "No account, no card.",
    to: "/free-lesson-generator",
    testid: "links-card-free-lesson",
  },
  {
    title: "Build a lesson from your own resource",
    sub: "Upload what you already use. Keep it.",
    to: "/lesson-from-resource",
    testid: "links-card-from-resource",
  },
  {
    title: "Classroom Support Hub",
    sub: "Strategies for the students who need something different.",
    to: "/classroom-support",
    testid: "links-card-classroom-support",
  },
  {
    title: "Free supply plans",
    sub: "For the morning you wake up sick.",
    to: "/supply-plans",
    testid: "links-card-supply-plans",
  },
  {
    title: "See plans",
    sub: "For teachers who want the full toolkit.",
    to: "/pricing",
    testid: "links-card-pricing",
  },
];

export default function LinksPage() {
  useDocumentMeta({
    title: "Atomic Edu — start here",
    description:
      "Free lesson generator, Transferable Skills posters, the Classroom Support Hub, free supply plans, and more — everything to get started, Ontario Grades 4–8.",
    image: "https://atomicedu.ca/og/links.png",
    url: "https://atomicedu.ca/links",
    type: "website",
  });
  useEffect(() => {
    // noindex only — the title + OG tags are owned by useDocumentMeta
    // above. This shim page must not compete with the real pages in
    // search, but it still needs a rich share card (it's the IG bio link).
    let robots = document.head.querySelector("meta[name='robots']");
    const createdRobots = !robots;
    const previousRobots = robots ? robots.getAttribute("content") : null;
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", "noindex");

    return () => {
      if (createdRobots) {
        robots.parentNode?.removeChild(robots);
      } else if (previousRobots !== null) {
        robots.setAttribute("content", previousRobots);
      }
    };
  }, []);

  return (
    <div
      data-testid="links-page"
      className="relative min-h-screen overflow-x-clip font-montserrat"
    >
      {/* Soft mint gradient — persistent accent blobs + grid, matching the
          homepage hero. Base page colour comes from the body (#f4f5f7). */}
      <div
        aria-hidden="true"
        className="fixed -top-32 -right-32 w-[44rem] h-[44rem] rounded-full bg-[#96ded1]/30 blur-3xl pointer-events-none z-0"
      />
      <div
        aria-hidden="true"
        className="fixed top-1/2 -left-40 w-[34rem] h-[34rem] rounded-full bg-[#96ded1]/15 blur-3xl pointer-events-none z-0"
      />
      <div
        aria-hidden="true"
        className="fixed -bottom-40 right-1/4 w-[30rem] h-[30rem] rounded-full bg-[#96ded1]/20 blur-3xl pointer-events-none z-0"
      />
      <div
        aria-hidden="true"
        className="fixed inset-0 ae-hero-grid opacity-50 pointer-events-none z-0"
      />

      {/* Slate-navy public header: logo mark + wordmark, no nav / hamburger.
          `relative z-10` keeps it above the fixed z-0 mint blobs so the navy
          reads pure (matching the homepage) instead of picking up a mint tint. */}
      <div className="relative z-10">
        <PublicPageHeader />
      </div>

      <main className="relative z-10 px-5 py-12 sm:py-16">
        <div className="mx-auto w-full max-w-[480px]">
          <header>
            {/* Homepage headline style (Montserrat 800, tight tracking,
                #4a6984) at H2 subtitle size — sits under the site header */}
            <h2 className="font-extrabold text-2xl sm:text-3xl text-[#4a6984] tracking-tight leading-[1.15]">
              Everything you need to get started, all in one place.
            </h2>
            {/* Body size, weight 500, muted text colour */}
            <p className="mt-3 text-base font-medium text-[#758d9f] leading-snug">
              Ontario curriculum, Grades 4&ndash;8. Built by a teacher, 20
              years in the classroom.
            </p>
          </header>

          {/* Destination cards */}
          <nav className="mt-10 flex flex-col gap-3.5">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className="flex items-center gap-3 min-h-[56px] rounded-xl bg-white px-5 py-4 shadow-[0_2px_10px_rgba(28,45,61,0.06)] hover:shadow-[0_4px_16px_rgba(28,45,61,0.10)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1c2d3d]/30 [transition:box-shadow_150ms_ease]"
              >
                <span className="flex-1 min-w-0">
                  <span className="block font-bold text-[17px] text-[#1c2d3d] tracking-tight leading-snug">
                    {l.title}
                  </span>
                  <span className="mt-1 block text-sm font-medium text-[#758d9f] leading-snug">
                    {l.sub}
                  </span>
                </span>
                <ChevronRight
                  className="h-5 w-5 shrink-0 text-[#758d9f]"
                  aria-hidden="true"
                />
              </Link>
            ))}
          </nav>
        </div>
      </main>
    </div>
  );
}
